import { useTranslation } from 'react-i18next';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { FLEET_TABS } from '@/Pages/tabRoutes';
import PrintSpoolPanel from './components/PrintSpoolPanel';
import DeviceRegistryPanel from './components/DeviceRegistryPanel';
import PrintersPanel from './components/PrintersPanel';
import RoutingPanel from './components/RoutingPanel';
import VersionPolicyPanel from './components/VersionPolicyPanel';

/**
 * §11 / ADR-0009 — the device, printer and hardware fleet.
 *
 * Five tabs, in the order an operator needs them rather than the order they
 * were built: the spool first (what is broken right now), then the registry,
 * then the hardware and routing that were configured once at install, then the
 * version floor.
 *
 * The whole surface reads as one estate on purpose. A print bridge is a
 * `Device` row *and* a `PrintBridge` credential, and the ADR is explicit that
 * it belongs in one fleet list rather than a second registry beside it — so the
 * bridges live on the printers tab, next to the hardware they own, and the
 * device registry links across to them.
 */
export default function Fleet() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/fleet', FLEET_TABS);

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('fleet.title')}</h1>
        <p className="text-sm text-ink-muted">{t('fleet.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
        value={current}
        onChange={select}
        label={t('fleet.title')}
        idPrefix="fleet"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {current === 'spool' && <PrintSpoolPanel />}
      {current === 'devices' && <DeviceRegistryPanel />}
      {current === 'printers' && <PrintersPanel />}
      {current === 'routing' && <RoutingPanel />}
      {current === 'versions' && <VersionPolicyPanel />}
    </div>
  );
}
